import type * as Struct from 'struct';
import type { Tender } from '~/models/tenders';
import type { FormComponentProps } from 'antd/lib/form';
import React, { useState } from 'react';
import { routeUrl, withPreload, withRouteX } from '~/router';
import { FormWrapper } from 'Components/form';
import { mergeSchema } from 'Components/form/utils';
import dayjs from 'dayjs';
import { Button, Form } from 'Components/nui';
import { useMountedState } from '~/hooks';
import { promisify } from 'es6-promisify';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import FilesField from 'Components/form/FilesField';
import * as services from '~/services';
import { useStoreState } from '~/store';
import { FormattedMessage } from 'react-intl';

type TenderProp = Record<'tender', Tender>;

interface IDetails {
  mode?: string;
}
const Details = withPreload({
  route: 'tender-edit-details',
  preload: services.tenders.schemas.tenderDetails(),
})<IDetails>(({ data: { tender, schema }, mode = 'edit' }) => {
  return (
    <div className="tender-edit-step details">
      <DetailsForm tender={tender} schema={schema} mode={mode} />
    </div>
  );
});

export default withRouteX({
  name: 'tender-edit-details',
  exact: true,
})(({ params: { mode } }) => {
  return <Details mode={mode} />;
});

type IUseFormWrapper = FormComponentProps &
  Partial<TenderProp> &
  Record<'schema', Struct.Schema>;
type FormProps = IUseFormWrapper & Record<'mode', string>;
function useFormWrapper({ form, schema, tender }: IUseFormWrapper) {
  const timeInterval = 5;
  const fromDt = dayjs()
    .startOf('hour')
    .minute(Math.ceil(dayjs().minute() / timeInterval) * timeInterval);

  const [files, setFiles] = useState([]);
  const reservePriceEnabled = useStoreState(
    (state) => state.auth.solutionSettings.tenderreserveprice
  );

  const fields = [
    {
      name: 'name',
      type: 'Input',
    },
    {
      name: 'issell',
      choices: [
        {
          key: 'sell',
          value: 'true',
          label: (
            <FormattedMessage
              id="tender-create-details-page-tender-type-option-sell"
              description="Label for Sell radio option on tenders create/edit details page"
              defaultMessage="Sell"
            />
          ),
        },
        {
          key: 'buy',
          value: 'false',
          label: (
            <FormattedMessage
              id="tender-create-details-page-tender-type-option-buy"
              description="Label for Buy radio option on tenders create/edit details page"
              defaultMessage="Buy"
            />
          ),
        },
      ],
      type: 'RadioSelect',
    },
    {
      render: () => (
        <div key="method-help" className="pt-10">
          <h3>
            <FormattedMessage
              id="tender-create-details-page-tender-method-heading"
              description="Heading for Tender method on tenders create/edit details page"
              defaultMessage="Tender method"
            />
          </h3>
          <p>
            <FormattedMessage
              id="tender-create-details-page-tender-method-subheading"
              description="Subheading for Tender method on tenders create/edit details page"
              defaultMessage="Select a tender method to continue."
            />
          </p>
          {reservePriceEnabled && (
            <p>
              <strong>
                <FormattedMessage
                  id="tender-create-details-page-tender-method-text-reserved-price"
                  description="Text for Tender method when reserved price is enabled on tenders create/edit details page"
                  defaultMessage="If a reserve price has been set and the tender does not meet the reserve price, the tender owner is not obligated to trade."
                />
              </strong>
            </p>
          )}
        </div>
      ),
    },
    {
      name: 'method',
      noLabel: true,
      choices: [
        {
          value: 'open',
          key: 'open',
          label: (
            <FormattedMessage
              id="tender-create-details-page-method-field-open-new"
              description="Label for Open option on tenders create/edit details page"
              defaultMessage="Open: Competing {counter, select, offer {offer} bid {bid} other {bid}} prices are visible to all participants."
              values={{
                counter:
                  form.getFieldValue('issell') === 'false' ? 'offer' : 'bid',
              }}
            />
          ),
        },
        {
          value: 'blind',
          key: 'blind',
          label: (
            <FormattedMessage
              id="tender-create-details-page-method-field-blind-new"
              description="Label for Blind option on tenders create/edit details page"
              defaultMessage="Blind: Participants only see their own {counter, select, offer {offers} bid {bids} other {bids}}."
              values={{
                counter:
                  form.getFieldValue('issell') === 'false' ? 'offer' : 'bid',
              }}
            />
          ),
        },
      ],
      type: 'RadioSelect',
    },
    {
      render: () => {
        const url =
          form.getFieldValue('issell') === 'false'
            ? 'https://www.nuimarkets.com/procurement-tender-rules'
            : 'https://www.nuimarkets.com/sales-tender-rules';
        return (
          <p key="tender-rules-link">
            <FormattedMessage
              id="tender-create-details-page-tender-method-rules-link"
              description="Link to tender rules below tender method options"
              defaultMessage="Refer to the <a>tender rules</a> for more detail on tender functionality."
              values={{
                a: (chunks) => (
                  <a href={url} target="_blank" rel="noopener noreferrer">
                    {chunks}
                  </a>
                ),
              }}
            />
          </p>
        );
      },
    },
    {
      render: () => <hr key="sep-0" className="mt-0" />,
    },
    {
      name: 'start',
      fromDate: fromDt.clone(),
      props: {
        getCalendarContainer: null,
        showTime: {
          format: 'h:mm a',
          minuteStep: timeInterval,
          hideDisabledOptions: true,
          defaultValue: fromDt.clone(),
        },
      },
      type: 'DateTime',
    },
    {
      name: 'duration',
      // temp - to be removed when provded by BE
      label: schema?.duration?.label || 'Duration',
      // temp - to be removed when provded by BE
      props: {
        placeholder: 'Select duration',
      },
    },
    {
      name: 'files',
      render() {
        if (!schema.files) return null;
        return (
          <FilesField
            key="form-files"
            label={
              <FormattedMessage
                id="documents-form-label"
                description="Label for Documents input form"
                defaultMessage="Documents"
              />
            }
            accept={schema.files.accepted.join(', ')}
            files={files}
            setFiles={setFiles}
          />
        );
      },
      serializeField() {
        if (!schema.files) return null;
        return files.length ? { files } : undefined;
      },
    },
    {
      name: 'attachments',
      choices:
        tender?.files?.map((f) => ({
          key: f.id,
          value: f.id,
          label: f.filename,
        })) || [],
      props: {
        placeholder: 'Select attachments to keep',
      },
      type: 'MultipleSelect',
    },
  ];

  const attachments = !!tender?.files?.length &&
    !!schema.files && { attachments: { type: 'MultipleSelect' } };

  return new FormWrapper(
    form,
    tender?.formdata,
    mergeSchema({ ...attachments, ...schema }, fields)
  );
}

const BaseForm = ({ form, schema, tender, mode }: FormProps) => {
  const [loading, setLoading] = useMountedState(false);
  const history = useHistory();

  const formWrapper = useFormWrapper({ form, schema, tender });

  async function handleSubmit() {
    setLoading(true);

    try {
      await promisify(form.validateFieldsAndScroll)();
    } catch {
      setLoading(false);
      return;
    }

    const { files, ...fields } = formWrapper.serialize();
    const formdata = new FormData();
    for (const [name, value] of Object.entries(fields)) {
      if (value !== undefined) {
        if (Array.isArray(value))
          for (const v of value) formdata.append(name, v);
        else formdata.append(name, value as string);
      }
    }
    if (files) for (const f of files) formdata.append('files[]', f);
    if (tender?.id) formdata.append('id', tender.id);
    const result = await services.tenders.actions.editTender(formdata);

    if (result) {
      if (result.success) {
        const name = fields.name;
        const action = fields.id ? 'updated' : 'created';
        toast.success(`Tender "${name}" ${action} successfully`);
        const url = routeUrl('tender-edit-orders', {
          tenderId: result.data.id,
          mode,
        });
        history.push(url);
      } else {
        if (result.errors) for (const e of result.errors) toast.error(e);
        if (result.validate) formWrapper.setErrors(result.validate);
      }
    }

    setLoading(false);
  }

  return (
    <Form
      className="nui-form tender-form"
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
    >
      {formWrapper.render()}
      <div className="sticky-btm button-set">
        <Button
          htmlType="submit"
          type="primary"
          disabled={loading}
          loading={loading}
        >
          <FormattedMessage
            id="tender-create-details-page-save-button"
            description="Button Save on tenders create/edit details page"
            defaultMessage="Save"
          />
        </Button>
      </div>
    </Form>
  );
};

const DetailsForm = Form.create<FormProps>()(BaseForm);
